import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import LucideIcon from "../../../components/LucideIcon";
import axios from "axios";
import moment from "moment";
import "moment-timezone";
import RswEditor, { EditorProvider } from "react-simple-wysiwyg";
import LoadingButton from "../../../components/LoadingButton";
import SmartListPicker from "../../../components/SmartListPicker";
import FieldMappingSelector from "../../../components/FieldMappingSelector";
import SendScheduleEditor, {
  ScheduleSettings,
  buildDefaultScheduleSettings,
} from "../../../components/SendScheduleEditor";
import { hostname, parseError } from "../../../utils";
import { fetchIntegrationsByType } from "../../integrations/api";
import { loadAllSenderProfiles } from "../../../components/SearchableSenderProfileSelect";
import {
  MARKETING_CAMPAIGN_SCHEDULE_NOW,
  MARKETING_CAMPAIGN_SCHEDULE_LATER,
} from "../../../constants";
import { MARKETING_EMAIL_INTEGRATION_TYPES } from "../../integrations/constants";
import { EmailBuilderTemplate } from "../../../components/EmailBuilder";
import VisualEmailBuilderDialog from "./VisualEmailBuilderDialog";

export interface Campaign {
  id: string;
  companyId: string;
  name: string;
  subject: string;
  fromName?: string | null;
  replyTo?: string | null;
  bodyHtml: string;
  bodyDocument?: EmailBuilderTemplate | null;
  senderProfileId?: string | null;
  listId?: string | null;
  fieldMappings?: { email?: string[] } & Record<string, string[] | undefined>;
  scheduleType: string;
  scheduledAt?: string | null;
  timezone?: string | null;
  scheduleSettings?: ScheduleSettings | null;
  status: string;
  sentCount?: number;
  failedCount?: number;
  skippedCount?: number;
  totalRecipients?: number;
  smartList?: { id: string; name: string };
  senderProfile?: {
    id: string;
    name?: string;
    email?: string;
    integrationId?: string | null;
  };
}

interface CampaignFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  onError: (err: string) => void;
  companyId: string;
  getToken: () => Promise<string>;
  openDialog: (title: string, content: string, buttonText: string, onClick: () => void) => void;
  // Accepts a full Campaign for edit, or a partial seed (without id) when
  // starting from a template.
  campaign?: Partial<Campaign> | null;
}

interface SenderProfileOption {
  id: string;
  name: string;
  email: string;
  integrationId?: string | null;
}

interface EmailIntegrationOption {
  id: string;
  type: string;
  name?: string;
}

interface ListHeaderOption {
  id: string;
  name: string;
  type?: string;
}

const CampaignFormDialog: React.FC<CampaignFormDialogProps> = ({
  open,
  onClose,
  onSaved,
  onError,
  companyId,
  getToken,
  openDialog,
  campaign,
}) => {
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [fromName, setFromName] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [replyToError, setReplyToError] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [bodyDocument, setBodyDocument] = useState<EmailBuilderTemplate | null>(null);
  const [visualDialogOpen, setVisualDialogOpen] = useState(false);
  const [senderProfileId, setSenderProfileId] = useState("");
  const [listId, setListId] = useState("");
  const [fieldMappings, setFieldMappings] = useState<{ email?: string[] }>({});
  const [showFieldMappings, setShowFieldMappings] = useState(false);
  const [scheduleType, setScheduleType] = useState<string>(
    MARKETING_CAMPAIGN_SCHEDULE_NOW
  );
  const [scheduleDate, setScheduleDate] = useState<any>(moment().add(1, "day"));
  const [scheduleHour, setScheduleHour] = useState<number>(9);
  const [scheduleMinute, setScheduleMinute] = useState<number>(0);
  const [scheduleAMPM, setScheduleAMPM] = useState<string>("AM");
  const [scheduleSettings, setScheduleSettings] = useState<ScheduleSettings>(
    buildDefaultScheduleSettings()
  );
  const [scheduleError, setScheduleError] = useState<string>("");
  const scheduleTimezone = scheduleSettings.timezone || moment.tz.guess();

  const computeScheduledAtIso = (): string | null => {
    if (!scheduleDate) return null;
    let hour24 = scheduleHour;
    if (scheduleAMPM === "PM" && scheduleHour !== 12) hour24 = scheduleHour + 12;
    else if (scheduleAMPM === "AM" && scheduleHour === 12) hour24 = 0;
    const m = moment.tz(
      `${scheduleDate.format("YYYY-MM-DD")} ${hour24
        .toString()
        .padStart(2, "0")}:${scheduleMinute.toString().padStart(2, "0")}`,
      "YYYY-MM-DD HH:mm",
      scheduleTimezone
    );
    return m.utc().toISOString();
  };

  const validateReplyTo = (): boolean => {
    const trimmed = replyTo.trim();
    if (!trimmed) {
      setReplyToError("");
      return true;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setReplyToError("Enter a valid email address");
      return false;
    }
    setReplyToError("");
    return true;
  };

  const validateSchedule = (): boolean => {
    if (scheduleType !== MARKETING_CAMPAIGN_SCHEDULE_LATER) {
      setScheduleError("");
      return true;
    }
    if (!scheduleDate) {
      setScheduleError("Please select a date");
      return false;
    }
    let hour24 = scheduleHour;
    if (scheduleAMPM === "PM" && scheduleHour !== 12) hour24 = scheduleHour + 12;
    else if (scheduleAMPM === "AM" && scheduleHour === 12) hour24 = 0;
    const scheduledTimeInTZ = moment.tz(
      `${scheduleDate.format("YYYY-MM-DD")} ${hour24
        .toString()
        .padStart(2, "0")}:${scheduleMinute.toString().padStart(2, "0")}`,
      "YYYY-MM-DD HH:mm",
      scheduleTimezone
    );
    if (scheduledTimeInTZ <= moment()) {
      setScheduleError("Scheduled time must be in the future");
      return false;
    }
    setScheduleError("");
    return true;
  };

  const [senderProfiles, setSenderProfiles] = useState<SenderProfileOption[]>([]);
  const [emailIntegrations, setEmailIntegrations] = useState<EmailIntegrationOption[]>([]);
  const [listHeaders, setListHeaders] = useState<ListHeaderOption[]>([]);

  useEffect(() => {
    if (!open) return;
    setName(campaign?.name || "");
    setSubject(campaign?.subject || "");
    setFromName(campaign?.fromName || "");
    setReplyTo(campaign?.replyTo || "");
    setReplyToError("");
    setBodyHtml(campaign?.bodyHtml || "");
    setBodyDocument(campaign?.bodyDocument || null);
    setSenderProfileId(campaign?.senderProfileId || "");
    setListId(campaign?.listId || "");
    setFieldMappings((campaign?.fieldMappings as any) || {});
    setScheduleType(campaign?.scheduleType || MARKETING_CAMPAIGN_SCHEDULE_NOW);

    const incomingSettings =
      (campaign?.scheduleSettings as ScheduleSettings) ||
      buildDefaultScheduleSettings();
    const tz =
      incomingSettings.timezone || campaign?.timezone || moment.tz.guess();
    setScheduleSettings({ ...incomingSettings, timezone: tz });
    if (campaign?.scheduledAt) {
      const m = moment.tz(campaign.scheduledAt, tz);
      setScheduleDate(m.clone().startOf("day"));
      const hour12 = m.hours() % 12 || 12;
      setScheduleHour(hour12);
      setScheduleMinute(m.minutes());
      setScheduleAMPM(m.hours() >= 12 ? "PM" : "AM");
    } else {
      setScheduleDate(moment().add(1, "day"));
      setScheduleHour(9);
      setScheduleMinute(0);
      setScheduleAMPM("AM");
    }
    setScheduleError("");
    setTestResult(null);
    setTestEmail("");
  }, [open, campaign]);

  // Load sender profiles and email integrations once the dialog opens
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const token = await getToken();
        const profiles = await loadAllSenderProfiles(token, companyId);
        setSenderProfiles(profiles as SenderProfileOption[]);
      } catch (err) {
        console.error("Failed to load sender profiles", err);
      }
      try {
        const integrations = await fetchIntegrationsByType(
          companyId,
          "email",
          getToken
        );
        setEmailIntegrations(integrations || []);
      } catch (err) {
        console.error("Failed to load email integrations", err);
      }
    })();
  }, [open, companyId, getToken]);

  // When a list is selected, load its headers so we can offer field mapping
  useEffect(() => {
    if (!listId) {
      setListHeaders([]);
      return;
    }
    (async () => {
      try {
        const token = await getToken();
        const res = await axios.get(`${hostname}/lists/${listId}`, {
          params: { companyId },
          headers: { Authorization: `Bearer ${token}` },
        });
        const listData = res.data?.list || res.data;
        const headers = (listData?.headers || []).filter(
          (h: any) => h.type !== "action"
        );
        setListHeaders(headers);
      } catch (err) {
        console.error("Failed to load list headers", err);
      }
    })();
  }, [listId, companyId, getToken]);

  const integrationTypeById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const i of emailIntegrations) map[i.id] = i.type;
    return map;
  }, [emailIntegrations]);

  const selectedSender = useMemo(
    () => senderProfiles.find((p) => p.id === senderProfileId),
    [senderProfiles, senderProfileId]
  );

  const senderIntegrationType = selectedSender?.integrationId
    ? integrationTypeById[selectedSender.integrationId]
    : undefined;

  const senderWarning =
    selectedSender &&
    senderIntegrationType &&
    !MARKETING_EMAIL_INTEGRATION_TYPES.includes(senderIntegrationType);

  const buildPayload = () => ({
    companyId,
    name,
    subject,
    fromName: fromName || null,
    replyTo: replyTo.trim() || null,
    bodyHtml,
    bodyDocument,
    senderProfileId: senderProfileId || null,
    listId: listId || null,
    fieldMappings,
    scheduleType,
    scheduledAt:
      scheduleType === MARKETING_CAMPAIGN_SCHEDULE_LATER
        ? computeScheduledAtIso()
        : null,
    timezone: scheduleTimezone,
    scheduleSettings,
  });

  const saveCampaign = useCallback(async (): Promise<Campaign | null> => {
    const token = await getToken();
    const payload = buildPayload();
    if (campaign?.id) {
      const res = await axios.patch(
        `${hostname}/marketing-campaigns/${campaign.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return res.data;
    }
    const res = await axios.post(
      `${hostname}/marketing-campaigns`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return res.data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign, getToken, companyId, name, subject, fromName, replyTo, bodyHtml, bodyDocument, senderProfileId, listId, fieldMappings, scheduleType, scheduleDate, scheduleHour, scheduleMinute, scheduleAMPM, scheduleTimezone]);

  const handleSave = async () => {
    if (!validateReplyTo()) return;
    setSaving(true);
    try {
      await saveCampaign();
      onSaved();
    } catch (err) {
      onError(parseError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndSchedule = async () => {
    if (!validateReplyTo()) return;
    if (!validateSchedule()) return;
    const scheduledAtIso = computeScheduledAtIso();
    if (!scheduledAtIso) {
      onError("Pick a scheduled send time first.");
      return;
    }
    setSaving(true);
    try {
      const saved = await saveCampaign();
      if (!saved) return;
      const token = await getToken();
      await axios.post(
        `${hostname}/marketing-campaigns/${saved.id}/schedule`,
        {
          companyId,
          scheduledAt: scheduledAtIso,
          timezone: scheduleTimezone,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSaved();
    } catch (err) {
      onError(parseError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleSendNow = () => {
    if (!validateReplyTo()) return;
    openDialog(
      "Send Campaign",
      "Send this campaign to the attached smart list now?",
      "Send",
      async () => {
        setSending(true);
        try {
          const saved = await saveCampaign();
          if (!saved) return;
          const token = await getToken();
          await axios.post(
            `${hostname}/marketing-campaigns/${saved.id}/send-now`,
            { companyId },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          onSaved();
        } catch (err) {
          onError(parseError(err));
        } finally {
          setSending(false);
        }
      }
    );
  };

  const handleTestSend = async () => {
    if (!testEmail) {
      setTestResult("Enter a test email address first.");
      return;
    }
    if (!validateReplyTo()) return;
    setTesting(true);
    setTestResult(null);
    try {
      const saved = await saveCampaign();
      if (!saved) return;
      const token = await getToken();
      const res = await axios.post(
        `${hostname}/marketing-campaigns/${saved.id}/test-send`,
        { companyId, testEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.success) {
        setTestResult(`Test email sent to ${testEmail}.`);
      } else {
        setTestResult(res.data?.error || "Test send failed.");
      }
    } catch (err) {
      setTestResult(parseError(err));
    } finally {
      setTesting(false);
    }
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {campaign?.id ? "Edit Campaign" : "New Marketing Campaign"}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <LucideIcon name="X" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="Campaign Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
            fullWidth
          />
          <TextField
            label="From Name (optional)"
            value={fromName}
            onChange={(e) => setFromName(e.target.value)}
            fullWidth
          />
          <TextField
            label="Reply-To Email (optional)"
            type="email"
            value={replyTo}
            onChange={(e) => {
              setReplyTo(e.target.value);
              if (replyToError) setReplyToError("");
            }}
            onBlur={validateReplyTo}
            error={!!replyToError}
            helperText={
              replyToError ||
              "Replies will go here instead of the sender profile's address."
            }
            fullWidth
          />

          <FormControl fullWidth required>
            <InputLabel>Sender Profile</InputLabel>
            <Select
              label="Sender Profile"
              value={senderProfileId}
              onChange={(e) => setSenderProfileId(e.target.value as string)}
            >
              {senderProfiles.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name ? `${p.name} <${p.email}>` : p.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {senderWarning && (
            <Alert severity="warning">
              The selected sender uses a personal inbox integration (
              {senderIntegrationType}). Personal inboxes have strict sending
              limits and aren't designed for bulk marketing. For larger sends,
              use a dedicated ESP like Amazon SES or SendGrid.
            </Alert>
          )}

          <SmartListPicker
            companyId={companyId}
            getToken={getToken}
            value={listId}
            onChange={setListId}
            label="Smart List"
            required
            showCount
          />

          {listHeaders.length > 0 && (
            <Accordion
              expanded={showFieldMappings}
              onChange={() => setShowFieldMappings(!showFieldMappings)}
              sx={{ boxShadow: "none", "&:before": { display: "none" } }}
              disableGutters
            >
              <AccordionSummary
                expandIcon={<LucideIcon name="ChevronDown" />}
                sx={{ px: 0, minHeight: "auto", "& .MuiAccordionSummary-content": { my: 1 } }}
              >
                <Typography variant="body2" color="text.secondary">
                  Advanced
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Email field mapping
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                  Pick which list column holds the email address. If left empty,
                  we'll auto-detect common email columns.
                </Typography>
                <FieldMappingSelector
                  fieldMappings={fieldMappings}
                  onChange={(m) => setFieldMappings(m)}
                  availableHeaders={listHeaders}
                  fieldTypes={["email"]}
                  showHelp={false}
                />
              </AccordionDetails>
            </Accordion>
          )}

          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography variant="subtitle2">Email Body</Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<LucideIcon name="PenTool" size={18} />}
                onClick={() => setVisualDialogOpen(true)}
              >
                {bodyDocument ? "Edit in Visual Builder" : "Open Visual Builder"}
              </Button>
            </Box>
            {bodyDocument ? (
              <Box
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  overflow: "hidden",
                  backgroundColor: "action.hover",
                  height: 300,
                }}
              >
                <iframe
                  title="Campaign preview"
                  srcDoc={bodyHtml}
                  sandbox=""
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                    backgroundColor: "#fff",
                  }}
                />
              </Box>
            ) : (
              <EditorProvider>
                <RswEditor
                  style={{ height: 300 }}
                  containerProps={{ style: { resize: "vertical" } }}
                  value={bodyHtml}
                  onChange={(e) => setBodyHtml(e.target.value)}
                />
              </EditorProvider>
            )}
          </Box>

          <Accordion disableGutters variant="outlined">
            <AccordionSummary expandIcon={<LucideIcon name="ChevronDown" />}>
              <Typography variant="subtitle2">Send Schedule</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <SendScheduleEditor
                value={scheduleSettings}
                onChange={setScheduleSettings}
                helperText="Emails will only be sent during these time windows, so recipients don't get messages at 1am."
              />
            </AccordionDetails>
          </Accordion>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Sending Options
            </Typography>
            <RadioGroup
              row
              value={scheduleType}
              onChange={(e) => {
                setScheduleType(e.target.value);
                setScheduleError("");
              }}
            >
              <FormControlLabel
                value={MARKETING_CAMPAIGN_SCHEDULE_NOW}
                control={<Radio />}
                label="Send Now"
              />
              <FormControlLabel
                value={MARKETING_CAMPAIGN_SCHEDULE_LATER}
                control={<Radio />}
                label="Schedule for Later"
              />
            </RadioGroup>
          </Box>

          {scheduleType === MARKETING_CAMPAIGN_SCHEDULE_LATER && (
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "2fr 1fr 1fr 1fr",
                    gap: 2,
                    mb: 2,
                    alignItems: "flex-start",
                  }}
                >
                  <DatePicker
                    label="Select Date"
                    value={scheduleDate}
                    onChange={(newDate) => {
                      setScheduleDate(newDate);
                      setScheduleError("");
                    }}
                    slotProps={{ textField: { fullWidth: true } }}
                  />

                  <FormControl fullWidth>
                    <InputLabel>Hour</InputLabel>
                    <Select
                      label="Hour"
                      value={scheduleHour}
                      onChange={(e) => {
                        setScheduleHour(Number(e.target.value));
                        setScheduleError("");
                      }}
                    >
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((hour) => (
                        <MenuItem key={hour} value={hour}>
                          {hour}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>Minute</InputLabel>
                    <Select
                      label="Minute"
                      value={scheduleMinute}
                      onChange={(e) => {
                        setScheduleMinute(Number(e.target.value));
                        setScheduleError("");
                      }}
                    >
                      {Array.from({ length: 60 }, (_, i) => i).map((minute) => (
                        <MenuItem key={minute} value={minute}>
                          {minute.toString().padStart(2, "0")}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>AM/PM</InputLabel>
                    <Select
                      label="AM/PM"
                      value={scheduleAMPM}
                      onChange={(e) => {
                        setScheduleAMPM(e.target.value as string);
                        setScheduleError("");
                      }}
                    >
                      <MenuItem value="AM">AM</MenuItem>
                      <MenuItem value="PM">PM</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 1 }}
                >
                  Using timezone <strong>{scheduleTimezone}</strong> from Send
                  Schedule.
                </Typography>

                {scheduleError && (
                  <Typography
                    color="error"
                    variant="caption"
                    display="block"
                    sx={{ mb: 1 }}
                  >
                    {scheduleError}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Campaign will be automatically sent at the selected date and time in your timezone
                </Typography>
              </Box>
            </LocalizationProvider>
          )}

          <Box sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Send a test
            </Typography>
            <Stack direction="row" spacing={1}>
              <TextField
                label="Test email address"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                size="small"
                fullWidth
              />
              <LoadingButton
                variant="outlined"
                loading={testing}
                onClick={handleTestSend}
              >
                Send
              </LoadingButton>
            </Stack>
            {testResult && (
              <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                {testResult}
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton loading={saving} onClick={handleSave} variant="outlined">
          Save
        </LoadingButton>
        {scheduleType === MARKETING_CAMPAIGN_SCHEDULE_LATER ? (
          <LoadingButton
            loading={saving}
            onClick={handleSaveAndSchedule}
            variant="contained"
          >
            Save &amp; Schedule
          </LoadingButton>
        ) : (
          <LoadingButton
            loading={sending}
            onClick={handleSendNow}
            variant="contained"
          >
            Save &amp; Send Now
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
    <VisualEmailBuilderDialog
      open={visualDialogOpen}
      onClose={() => setVisualDialogOpen(false)}
      companyId={companyId}
      getToken={getToken}
      name={name}
      initialBodyDocument={bodyDocument}
      listHeaders={listHeaders}
      onSave={(html, document) => {
        setBodyHtml(html);
        setBodyDocument(document);
      }}
      onError={onError}
    />
    </>
  );
};

export default CampaignFormDialog;
